import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  emailTemplateLibraryService,
  type HistoryEntryType,
  type TemplateHistoryEntry,
} from '@/services/email-template-library.service';

/** Merged timeline: this workspace's versions interleaved with platform releases. */
const ENTRY_LABEL: Record<HistoryEntryType, { label: string; className: string }> = {
  PLATFORM_INITIAL: { label: 'Platform initial', className: 'bg-muted text-muted-foreground' },
  PLATFORM_PUBLISHED: { label: 'Platform release', className: 'bg-accent/20 text-foreground' },
  CUSTOMIZED: { label: 'You published', className: 'bg-primary/10 text-primary' },
  UPDATED_FROM_PLATFORM: {
    label: 'You adopted a platform update',
    className: 'bg-primary/10 text-primary',
  },
  RESET_TO_PLATFORM: {
    label: 'You reset to platform',
    className: 'bg-muted text-muted-foreground',
  },
};

export function HistoryTimeline({ templateKey }: { templateKey: string }) {
  const [entries, setEntries] = useState<TemplateHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    emailTemplateLibraryService
      .history(templateKey)
      .then(setEntries)
      .catch((e: Error) => setError(e.message || 'The history could not be loaded.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [templateKey]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
        <p className="text-sm text-foreground">{error}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  if (entries.length === 0) {
    return <p className="py-16 text-center text-sm text-muted-foreground">No history yet.</p>;
  }

  return (
    <ol className="relative space-y-3 border-l border-border pl-5">
      {entries.map((e, i) => {
        const meta = ENTRY_LABEL[e.type] ?? ENTRY_LABEL.CUSTOMIZED;
        return (
          <li key={`${e.type}-${e.versionNumber}-${i}`} className="relative">
            <span className="absolute -left-[23px] top-2 h-2.5 w-2.5 rounded-full bg-primary" />
            <div className="rounded-lg border border-border bg-card p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                  className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', meta.className)}
                >
                  {meta.label} · v{e.versionNumber}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(e.occurredAt).toLocaleString()}
                </span>
              </div>
              <p className="mt-1.5 break-words text-xs text-foreground">{e.subject}</p>
              {e.changeNote && (
                <p className="mt-1 text-[11px] text-muted-foreground">{e.changeNote}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
